import { formatStorageBytes, type BuildingStorageQuota } from "../services/buildingStorageQuota";

export type StorageState = "normal" | "near" | "full" | "unlimited";

export function storageState(quota: BuildingStorageQuota): StorageState {
  if (quota.limitBytes === null) return "unlimited";
  if (quota.usedBytes >= quota.limitBytes) return "full";
  return quota.usedBytes >= quota.limitBytes * 0.85 ? "near" : "normal";
}

export function uploadsBlocked(quota: BuildingStorageQuota): boolean {
  return (
    quota.limitBytes !== null && (storageState(quota) === "full" || quota.availableBytes === 0)
  );
}

export function availableStorageLabel(quota?: BuildingStorageQuota) {
  return formatStorageBytes(quota?.availableBytes ?? 0);
}

export const storageCopy = {
  en: {
    label: "Building storage",
    usage: "{used} of {total} used",
    fullUsage: "Storage full · {used} of {total}",
    unlimitedUsage: "{used} used · ∞ Unlimited",
    nearLink: "Storage nearly full →",
    freeSpace: "Free up space →",
    more: "Get more space",
    moreRestricted: "Ask your organisation owner to change this building’s plan.",
    largest: "Review largest files",
    dismiss: "Dismiss storage warning",
    unavailable: "Storage usage unavailable",
    refreshFailed: "Storage usage could not be refreshed. Showing the last known usage.",
    nearBanner:
      "Storage is nearly full · {remaining} remaining. Uploads will fail when it runs out.",
    fullBanner:
      "Storage is full · Further uploads are blocked. Remove files you no longer need or get more storage.",
    nearUpload: "{remaining} left in this building’s storage — large uploads may not fit.",
    fullUpload:
      "Storage is full · {remaining} available. Remove files you no longer need or get more storage.",
    reservedUpload:
      "Uploads in progress have reserved the remaining space. Wait for them to finish, then check the available space before adding more files.",
    nearBubble: "Upload complete · storage is nearly full — {remaining} left.",
    fullBubble: "Upload complete · storage is now full — further uploads are blocked.",
  },
  et: {
    label: "Hoone salvestusruum",
    usage: "{used} / {total} kasutatud",
    fullUsage: "Salvestusruum täis · {used} / {total}",
    unlimitedUsage: "{used} kasutatud · ∞ Piiramatu",
    nearLink: "Salvestusruum peaaegu täis →",
    freeSpace: "Vabasta ruumi →",
    more: "Hangi ruumi juurde",
    moreRestricted: "Palu organisatsiooni omanikul selle hoone paketti muuta.",
    largest: "Vaata suurimaid faile",
    dismiss: "Sulge salvestusruumi hoiatus",
    unavailable: "Salvestusruumi kasutus pole saadaval",
    refreshFailed:
      "Salvestusruumi kasutust ei õnnestunud värskendada. Kuvatakse viimased teadaolevad andmed.",
    nearBanner:
      "Salvestusruum on peaaegu täis · {remaining} vaba. Ruumi lõppedes üleslaadimised ebaõnnestuvad.",
    fullBanner:
      "Salvestusruum on täis · Edasised üleslaadimised on blokeeritud. Eemalda failid, mida sa enam ei vaja, või hangi salvestusruumi juurde.",
    nearUpload:
      "Selle hoone salvestusruumis on {remaining} vaba — suured failid ei pruugi ära mahtuda.",
    fullUpload:
      "Salvestusruum on täis · {remaining} vaba. Eemalda failid, mida sa enam ei vaja, või hangi salvestusruumi juurde.",
    reservedUpload:
      "Pooleliolevad üleslaadimised on ülejäänud ruumi reserveerinud. Oota nende lõppemiseni ja kontrolli enne uute failide lisamist vaba ruumi.",
    nearBubble: "Üleslaadimine lõpetatud · salvestusruum on peaaegu täis — {remaining} vaba.",
    fullBubble:
      "Üleslaadimine lõpetatud · salvestusruum on nüüd täis — edasised üleslaadimised on blokeeritud.",
  },
} as const;
